import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { CircularProgress } from 'material-ui';
import { v4 as uuidv4 } from 'uuid';
import * as immer from 'immer';

import Pagination from 'components/Libs/Pagination';
import TcIndexTable from './TcIndexTable';
import Filters from './Filters';
import _ from 'lodash';
import api from 'services';

import { getTcHireIdeas } from 'actions/tc/indexes';
import { addNotification } from 'actions/notifications/notifications';
import styles from 'styles';
import { notifications } from './common';
import { QueryClient, QueryClientProvider } from 'react-query';
import Misc from './Misc';

export const VesselDwtTypesContext = React.createContext(undefined);
export const PredefinedIndexesContext = React.createContext(undefined);
export const ChangeFieldsContext = React.createContext(undefined);
export const RemoveRowContext = React.createContext(undefined);
export const AddUpdateContext = React.createContext({});
export const ViasContext = React.createContext(undefined);
export const HandleUpdateErrorsContext = React.createContext(undefined);
export const ErrorsContext = React.createContext([]);

const queryClient = new QueryClient();
const getDefaultFilters = () => ({ confirmed: true });

function TcIndexPage({
    tcHireIdeas, total, getTcHireIdeas, isLoaded, addNotification, 
}) {
    const rowsPerPage = [25, 50, 100];
    const [rows, setRows] = useState([]);
    const [rowsChanged, setRowsChanged] = useState([]);
    const [page, setPage] = useState(1);
    const [numberOfRows, setNumberOfRows] = useState(25);
    const [vesselDwtTypes, setVesselDwtTypes] = useState([]);
    const [predefinedIndexes, setPredefinedIndexes] = useState([]);
    const [vias, setVias] = useState([]);
    const [errors, setErrors] = useState([]);
    const [filters, setFilters] = useState(getDefaultFilters());

    useEffect(() => {
        setRows(tcHireIdeas);
        setErrors(tcHireIdeas.map(r => getDefaultError(r)));
    }, [tcHireIdeas]);

    useEffect(() => {
        api.getVesselDwtTypes()
            .then((res) => {
                setVesselDwtTypes(res.data);
            });

        api.getPredefinedIndexes()
            .then((res) => {
                setPredefinedIndexes(res.data);
            });
        api.getVias()
            .then((res) => {
                setVias(res.data);
            });
    }, []);

    const handleUpdatePagination = ({ page: p, numberOfRows: n }) => {
        setPage(p);
        setNumberOfRows(n);
        setRowsChanged([]);
    };

    useEffect(() => {
        setTimeout(() => {
            getTcHireIdeas({ 
                page, 
                limit: numberOfRows,
                ...filters,
            }); 
        }, 200); // tiny delay, for the state to be updated before data fetch (only for visual)
    }, [filters, page, numberOfRows]);

    const renderPagination = (
        <Pagination
            total={total}
            rowsPerPage={rowsPerPage}
            page={page}
            numberOfRows={numberOfRows}
            updateRows={handleUpdatePagination}
        />
    );

    const changeFieldsMultiple = (changes) => {
        const newRows = immer.produce(rows, (_rows) => {
            changes.forEach((change) => {
                const { index, field, value } = change;
                _.set(_rows[index], field, value);
            });
        });

        const newChanged = immer.produce(rowsChanged, (_rowsChanged) => {
            changes.forEach((change) => {
                const { index } = change;
                const alreadyInChanged = _rowsChanged.findIndex(rC => rC._id === newRows[index]._id);
    
                if (alreadyInChanged >= 0) {
                    _rowsChanged.splice(alreadyInChanged, 1);
                }

                _rowsChanged.push(newRows[index]);
            });
        });

        setRows(newRows);
        setRowsChanged(newChanged);
    };

    const removeRow = (rowId) => {
        const newRows = immer.produce(rows, (_rows) => {
            _rows.splice(rowId, 1);
        });

        const newErrors = immer.produce(errors, (_errors) => {
            _errors.splice(rowId, 1);
        });

        setRows(newRows);
        setErrors(newErrors);
    };

    const changeFilters = (value) => {
        setFilters({ ...filters, page: 1, ...value });
    };

    const clearFilters = () => {
        setFilters(getDefaultFilters());
        setPage(1);
    };

    const addRow = () => {
        const defaultHireIdea = getDefaultHireIdea();
        setRows([defaultHireIdea, ...rows]);
        setRowsChanged([...rowsChanged, defaultHireIdea]);
        setErrors([getDefaultError({ _id: defaultHireIdea.unique_id }, { isNew: true }), ...errors]);
    };

    const updateRows = () => {
        if (!rowsChanged.length) return;

        if (errors.find(e => Object.values(_.omit(e, ['_id'])).find(_e => _e))) {
            addNotification(notifications.error('Please fill in all required fields.'));
            return;
        }

        const toUpdate = rowsChanged.map((change) => {
            const changeForSave = _.cloneDeep(change);

            if (!changeForSave.vesselId) delete changeForSave.vesselId;
            return {
                ...changeForSave,
                regionFrom: changeForSave.regionFrom._id,
                regionTo: changeForSave.regionTo._id,
                placeFrom: changeForSave.placeFrom ? changeForSave.placeFrom._id : undefined,
                placeTo: changeForSave.placeTo ? changeForSave.placeTo._id : undefined,
                via: changeForSave.via ? changeForSave.via._id : undefined,
                vesselDwtGroup: changeForSave.vesselDwtGroup._id,
            };
        });

        api.updateHireIdea({ update: toUpdate })
            .then((res) => {
                if (!res.errors || !res.errors.length) {
                    addNotification(notifications.success('Added successfully.'));
                    getTcHireIdeas({ page, limit: numberOfRows, ...filters });
                    setRowsChanged([]);
                } else {
                    addNotification(notifications.error(res.errors[0].message));
                }
            });
    };

    const handleUpdateErrors = async (index, field, value) => {
        if (errors.length) {
            setErrors((prev) => {
                const newErrors = immer.produce(prev, (_errors) => {
                    if (rows[index].typeByPurpose) {
                        _.set(_errors[index], 'typeGeneral', false);
                    }
                    _errors[index][field] = value;
                });
                return newErrors;
            });
        }
    };

    return (
        <div>
            <div className='table-header'>
                <div className='item' />
                <div className='item' style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {renderPagination}

                    <QueryClientProvider client={queryClient}>
                        <Misc filters={filters} />
                    </QueryClientProvider>
                </div>
            </div>
            <div className="table-header">
                <AddUpdateContext.Provider value={{ addRow, updateRows }}>
                    <VesselDwtTypesContext.Provider value={vesselDwtTypes}>
                        <Filters
                            filters={filters}
                            changeFilters={changeFilters}
                            clearFilters={clearFilters}
                        />
                    </VesselDwtTypesContext.Provider>
                </AddUpdateContext.Provider>
            </div>
            <CircularProgress
                className={!isLoaded ? '' : 'hidden'}
                style={styles.circularProgress}
            />
            <div className={isLoaded ? '' : 'hidden'}>
                <div className="table-body">
                    <ChangeFieldsContext.Provider value={changeFieldsMultiple}>
                        <RemoveRowContext.Provider value={removeRow}>
                            <VesselDwtTypesContext.Provider value={vesselDwtTypes}>
                                <PredefinedIndexesContext.Provider value={predefinedIndexes}>
                                    <HandleUpdateErrorsContext.Provider value={handleUpdateErrors}>
                                        <ViasContext.Provider value={vias}>
                                            <TcIndexTable
                                                rows={rows}
                                                errors={errors}
                                            />
                                        </ViasContext.Provider>
                                    </HandleUpdateErrorsContext.Provider>
                                </PredefinedIndexesContext.Provider>
                            </VesselDwtTypesContext.Provider>
                        </RemoveRowContext.Provider>
                    </ChangeFieldsContext.Provider>
                </div>
            </div>
        </div>
    );
}

function getDefaultHireIdea() {
    const unique_id = uuidv4();  

    function generateEndAt() {
        const date = new Date();

        const endDate = new Date(date.toISOString().slice(0, 10) + 'T23:59:59.000Z');
    
        const distance = 7 - new Date(date.toISOString().slice(0, 10) + 'T00:00:00.000Z').getDay();
    
        endDate.setDate(endDate.getDate() + distance);
    
        return endDate;
    }

    return {
        key: unique_id,
        unique_id,
        can: generateEndAt(),
        clean: false,
        confirmed: true,
        rejected: false,
        lay: new Date(),
        roundTrip: false,
        vessel: {},
        vesselDwtGroup: {},
    };
}

function getDefaultError(r, { isNew = false } = {}) {
    const value = !!isNew;
    return {
        _id: r._id || r.unique_id,
        tcIndex: value,
        layCan: false,
        typeGeneral: value,
        vesselDwtGroup: value,
        regionFrom: value,
        regionTo: value,
    };
}

export default connect(
    state => ({
        isLoaded: state.tcIndexes.isLoaded,
        tcHireIdeas: state.tcIndexes.items,
        total: state.tcIndexes.pageInfo ? state.tcIndexes.pageInfo.count : 0,
    }),
    dispatch => bindActionCreators({
        getTcHireIdeas,
        addNotification,
    }, dispatch),
)(TcIndexPage);



// WEBPACK FOOTER //
// ./app/components/TcIndex/TcIndexPage.jsx